import React from "react";

import MeIcon from "@uui/User02";
import ParticipantsIcon from "@uui/Users03";
import { filter, reject } from "lodash/fp";

import MailIcon from "@app/common/assets/icons/MailIcon.png";
import { PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type { UserDistributionSetting } from "@app/common/types";
import type { MessagingBotProviderId } from "@app/common/types/providerTypes";

import type { DropdownItem } from "@rui/Dropdown/types";

export const ICON_SRC_FOR_CHANNEL: {
  [key in UserDistributionSetting["method"]]: string;
} = {
  email: MailIcon,
  slack: PROVIDERS_BY_ID.slack_bot.icon,
  teams: PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.icon,
};

export const ICON_OPTS_FOR_DISTRIBUTION_RECIPIENT: {
  [key in UserDistributionSetting["sendTo"]]: {
    Icon: typeof ParticipantsIcon;
    color: React.CSSProperties["color"];
    bgcolor: React.CSSProperties["backgroundColor"];
  } | null;
} = {
  me: {
    Icon: MeIcon,
    color: "shades.primary.500",
    bgcolor: "shades.ghost.500",
  },
  participants_with_access: {
    Icon: ParticipantsIcon,
    color: "chart.teal.dark",
    bgcolor: "#E2F9FB",
  },
  custom: null,
};

export const DISTRIBUTION_RECIPIENT_OPTIONS: DropdownItem<
  UserDistributionSetting["sendTo"]
>[] = [
  { value: "me", label: "Only me" },
  { value: "participants_with_access", label: "Participants with access" },
  { value: "custom", label: "Send to a channel" },
];

export const FREQUENCIES: DropdownItem<UserDistributionSetting["frequency"]>[] =
  [
    { value: "instant", label: "As it happens" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
  ];

export interface DistributionChannelConfig {
  channel: UserDistributionSetting["method"];
  label: string;
  recipients: typeof DISTRIBUTION_RECIPIENT_OPTIONS;
  frequencies: typeof FREQUENCIES;
  providerId?: MessagingBotProviderId;
  tooltip?: string;
}

export const MEETING_REPORT_DISTRIBUTION_CHANNELS: DistributionChannelConfig[] =
  [
    {
      channel: "email",
      label: "Email",
      recipients: reject({ value: "custom" }, DISTRIBUTION_RECIPIENT_OPTIONS),
      frequencies: filter({ value: "instant" }, FREQUENCIES),
    },
    {
      channel: "slack",
      label: "Slack",
      recipients: DISTRIBUTION_RECIPIENT_OPTIONS,
      frequencies: filter({ value: "instant" }, FREQUENCIES),
      providerId: "slack_bot",
      tooltip:
        "Slack recaps can only be distributed to people in your Slack workspace",
    },
    {
      channel: "teams",
      label: "Teams",
      recipients: DISTRIBUTION_RECIPIENT_OPTIONS,
      frequencies: filter({ value: "instant" }, FREQUENCIES),
      providerId: "microsoft_graph_teams_push",
      tooltip:
        "Teams recaps can only be distributed to people in your Teams workspace",
    },
  ];

export const PRE_READ_DISTRIBUTION_CHANNELS: DistributionChannelConfig[] = [
  {
    channel: "email",
    label: "Email",
    recipients: reject({ value: "custom" }, DISTRIBUTION_RECIPIENT_OPTIONS),
    frequencies: filter({ value: "instant" }, FREQUENCIES),
  },
  {
    channel: "slack",
    label: "Slack",
    recipients: DISTRIBUTION_RECIPIENT_OPTIONS,
    frequencies: filter({ value: "instant" }, FREQUENCIES),
    providerId: "slack_bot",
    tooltip:
      "Slack Pre-Reads can only be distributed to people in your Slack workspace",
  },
  {
    channel: "teams",
    label: "Teams",
    recipients: DISTRIBUTION_RECIPIENT_OPTIONS,
    frequencies: filter({ value: "instant" }, FREQUENCIES),
    providerId: "microsoft_graph_teams_push",
    tooltip:
      "Teams Pre-Reads can only be distributed to people in your Teams workspace",
  },
];

export const TOPIC_REPORT_DISTRIBUTION_CHANNELS: DistributionChannelConfig[] = [
  {
    channel: "email",
    label: "Email",
    recipients: filter({ value: "me" }, DISTRIBUTION_RECIPIENT_OPTIONS),
    frequencies: filter({ value: "daily" }, FREQUENCIES),
  },
  {
    channel: "slack",
    label: "Slack",
    recipients: filter({ value: "me" }, DISTRIBUTION_RECIPIENT_OPTIONS),
    frequencies: filter({ value: "daily" }, FREQUENCIES),
    providerId: "slack_bot",
  },
  {
    channel: "teams",
    label: "Teams",
    recipients: filter({ value: "me" }, DISTRIBUTION_RECIPIENT_OPTIONS),
    frequencies: filter({ value: "daily" }, FREQUENCIES),
    providerId: "microsoft_graph_teams_push",
  },
];

export const RECOMMENDATION_DISTRIBUTION_CHANNELS: DistributionChannelConfig[] =
  [
    {
      channel: "email",
      label: "Email",
      recipients: filter({ value: "me" }, DISTRIBUTION_RECIPIENT_OPTIONS),
      frequencies: filter({ value: "daily" }, FREQUENCIES),
    },
  ];
